/* ========================================================================= *
 * TRADE LINKS — the one model the worklist, the Command Center rows and the
 * governing-body page read. PURE: no React, no fetch.
 *
 * Brian, 2026-10-19: *"The trades should be surfaced in the worklist, the
 * Command Center and governing bodies. There is nothing linking this, so the
 * board cannot be read."*
 *
 * ⚠️ THIS FILE NEVER RESOLVES A SCOPE. `GET /api/trade-desk/links` hands over
 * the record ids the DESK resolved (qualifier-aware body matching, live
 * ownership). Re-deriving "which records are in TAPPS" here would be a second
 * resolver, and the first time the two disagreed a record would be under a
 * trade on the desk and under none on its row. Join on ids; nothing else.
 *
 * ⚠️ EVERY SURFACE RECONCILES. A trade is `linked` or `flagged`, never
 * neither, and each surface states trades = linked + flagged beside what it
 * shows — so a surface quietly shorter than the ledger cannot pass as whole.
 * ========================================================================= */

const S = (v) => (v == null ? '' : String(v)).trim()
const lower = (v) => S(v).toLowerCase()

export const LINK = Object.freeze({ LINKED: 'linked', FLAGGED: 'flagged' })
export const SOURCE = Object.freeze({ DESK: 'desk-offer', PROPOSAL: 'record-proposal' })

/* What still needs somebody, versus what has already happened. */
const ACTIVE = new Set(['open', 'proposed'])
const SETTLED = new Set(['claimed', 'accepted'])
export const isActive = (t) => ACTIVE.has(lower(t?.state))
export const isSettled = (t) => SETTLED.has(lower(t?.state))

export const RECONCILE_FAILED =
  'THESE TRADES DO NOT ADD UP — linked plus flagged is not the total, so this surface is '
  + 'dropping or double-counting a trade. Do not act on the counts until it is explained.'

/** Normalise the payload and RE-ASSERT the reconciliation client-side. */
export function normalizeLinks(payload) {
  const p = payload || {}
  const trades = Array.isArray(p.trades) ? p.trades : []
  const linked = trades.filter((t) => t.link === LINK.LINKED).length
  const flagged = trades.filter((t) => t.link === LINK.FLAGGED).length
  const server = p.totals || {}
  const reconciles = trades.length === linked + flagged
    && (server.trades == null || Number(server.trades) === trades.length)
    && (server.linked == null || Number(server.linked) === linked)
  return {
    trades,
    byRecord: p.byRecord && typeof p.byRecord === 'object' ? p.byRecord : {},
    byBody: Array.isArray(p.byBody) ? p.byBody : [],
    totals: { trades: trades.length, linked, flagged, reconciles,
      active: trades.filter(isActive).length, settled: trades.filter(isSettled).length,
      recordsUnderTrade: Object.keys(p.byRecord || {}).length },
    read: p.read || null,
    viewer: p.viewer || null,
  }
}

const byId = (links) => {
  const m = new Map()
  for (const t of links?.trades || []) m.set(t.id, t)
  return m
}

/** The trades on one record, active first. */
export function tradesOnRecord(links, recordId) {
  const id = S(recordId)
  if (!id || !links?.byRecord?.[id]) return []
  const m = byId(links)
  return links.byRecord[id].map((e) => m.get(e.tradeId)).filter(Boolean)
    .sort((a, b) => Number(isActive(b)) - Number(isActive(a)))
}

/** The inline status a Command Center row shows. `null` = nothing to say. */
export function chipFor(trades = []) {
  if (!trades.length) return null
  const t = trades[0]
  const more = trades.length > 1 ? ` (+${trades.length - 1} more)` : ''
  const flagged = trades.some((x) => x.link === LINK.FLAGGED)
  let label
  let tone
  if (isActive(t)) {
    label = t.source === SOURCE.DESK
      ? `On the Trade Desk — offered by ${t.fromRep || 'a rep'}`
      : `Trade proposed — ${t.fromRep || 'a rep'} → ${t.toRep || 'a rep'}`
    tone = t.windowState === 'closed_at_event' ? 'warn' : 'info'
    if (t.windowState === 'closed_at_event') label += ' · executive move only'
  } else if (isSettled(t)) {
    label = `Traded${t.toRep ? ` to ${t.toRep}` : ''}`
    tone = 'ok'
  } else {
    label = `Trade ${lower(t.state) || 'closed'}`
    tone = 'muted'
  }
  if (flagged) tone = 'bad'
  return {
    label: label + more,
    tone,
    flagged,
    title: [t.scopeLabel, t.reason ? `“${t.reason}”` : null,
      flagged ? `Needs fixing: ${trades.flatMap((x) => x.flags || []).map((f) => f.says).join(' ')}` : null]
      .filter(Boolean).join(' · '),
    to: t.source === SOURCE.DESK ? `/trade-desk?offer=${encodeURIComponent(t.id)}` : '/my-worklist',
    fix: flagged ? (trades.flatMap((x) => x.flags || [])[0]?.fix || null) : null,
  }
}

const daysText = (n) => (typeof n !== 'number' ? null
  : n < 0 ? 'the championship has happened'
    : n === 0 ? 'the championship is today' : `${n} day${n === 1 ? '' : 's'} to the championship`)

/**
 * THE WORK ORDERS — an ORDER and a REASON, per the-work-order-pattern.
 *
 * Every trade lands in exactly one bucket for this viewer, and the buckets are
 * returned with the orders so the page can state that they sum to the total:
 *   orders    work for this person (or for the team, when `who` is '*')
 *   settled   already happened — history, not work
 *   others    active, but somebody else's to act on
 */
export function ordersFor(links, { who = '', isExecutive = false } = {}) {
  const me = lower(who)
  const team = !me || me === '*'
  const orders = []
  let settled = 0
  let others = 0
  for (const t of links?.trades || []) {
    const mine = me && (lower(t.fromRep) === me || lower(t.toRep) === me
      || (t.records || []).some((r) => lower(r.holder) === me || lower(r.planRep) === me))
    const flagged = t.link === LINK.FLAGGED
    const o = orderOf(t, { me, team, mine, isExecutive })
    if (o && (team || mine || (flagged && isExecutive))) { orders.push(o); continue }
    if (!isActive(t) && !flagged) { settled += 1; continue }
    others += 1
  }
  orders.sort((a, b) => a.rank - b.rank || (a.days ?? 1e9) - (b.days ?? 1e9))
  return { orders, settled, others, total: (links?.trades || []).length,
    sums: orders.length + settled + others === (links?.trades || []).length }
}

function orderOf(t, { me, team, mine }) {
  const flagged = t.link === LINK.FLAGGED
  const days = typeof t.daysToEvent === 'number' ? t.daysToEvent : null
  const when = daysText(days)
  const scope = t.scopeLabel || 'a trade'
  const why = t.reason ? `“${t.reason}”` : 'No reason was recorded.'
  const desk = `/trade-desk?offer=${encodeURIComponent(t.id)}`
  const firstRec = t.recordIds?.[0]
  const recordLink = firstRec ? `/command-center?record=${encodeURIComponent(firstRec)}` : null
  const base = { id: t.id, trade: t, days, flagged, link: t.link }

  if (flagged) {
    const f = t.flags[0]
    return { ...base, rank: 0, verb: `Fix this trade — ${scope}`,
      reason: `${f.says} ${f.fix?.why || ''}`.trim(),
      ctas: [f.fix?.to ? { label: f.fix.label, to: f.fix.to, primary: true }
        : { label: f.fix?.label || 'Read again', retry: true, primary: true },
        recordLink && { label: 'Open the record', to: recordLink }].filter(Boolean) }
  }
  if (!isActive(t)) return null

  if (t.source === SOURCE.DESK) {
    const offeredByMe = me && lower(t.fromRep) === me
    if (offeredByMe) {
      return { ...base, rank: 2, verb: `Your offer is waiting for a claimer — ${scope}`,
        reason: `You offered it: ${why}${when ? ` · ${when}` : ''}. Withdraw it if you no longer mean it.`,
        ctas: [{ label: 'Open it on the Trade Desk', to: desk, primary: true },
          recordLink && { label: 'Open the record', to: recordLink }].filter(Boolean) }
    }
    return { ...base, rank: t.windowState === 'closed_at_event' ? 1 : 1.5,
      verb: t.windowState === 'closed_at_event'
        ? `Executive move needed — ${scope}`
        : `Claim or counter — ${scope}`,
      reason: `${t.fromRep || 'A rep'} is offering it: ${why}${when ? ` · ${when}` : ''}.`
        + (team ? '' : mine ? ' It touches records you hold or are named for.' : ''),
      ctas: [{ label: 'Open it on the Trade Desk', to: desk, primary: true },
        recordLink && { label: 'Open the record', to: recordLink }].filter(Boolean) }
  }
  /* A one-record proposal. */
  const toMe = me && lower(t.toRep) === me
  return { ...base, rank: toMe ? 1 : 2,
    verb: toMe ? `Accept or decline — ${t.records?.[0]?.name || scope}`
      : `Trade proposed — ${t.fromRep || 'a rep'} → ${t.toRep || 'a rep'}`,
    reason: `${why} The decision is on the record's card in the worklist of ${t.toRep || 'the receiving rep'}.`,
    ctas: [recordLink && { label: 'Open the record', to: recordLink, primary: true },
      { label: 'Open the worklist', to: '/my-worklist' }].filter(Boolean) }
}

/**
 * The trades that touch a set of record ids — a governing-body card passes the
 * SAME rows its own count and stamp use, so its trade figure is over the same
 * records and cannot disagree with the card.
 */
export function tradesForRecords(links, ids = []) {
  const want = new Set(ids.map(S))
  const out = []
  for (const t of links?.trades || []) {
    const n = (t.recordIds || []).filter((id) => want.has(S(id))).length
    if (n) out.push({ trade: t, n })
  }
  const linked = out.filter((x) => x.trade.link === LINK.LINKED).length
  return { items: out, total: out.length, linked, flagged: out.length - linked,
    records: [...new Set(out.flatMap((x) => (x.trade.recordIds || []).filter((id) => want.has(S(id)))))].length }
}
